import React from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import CandlestickBar from './CandlestickBar';

const TOOLTIP_STYLE = { background: '#12141A', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '0' };
const TOOLTIP_LABEL = { color: '#94A3B8', fontFamily: 'JetBrains Mono' };
const TOOLTIP_ITEM = { fontFamily: 'JetBrains Mono' };
const MAIN_MARGIN = { top: 10, right: 30, left: 0, bottom: 0 };
const MINI_MARGIN = { top: 5, right: 10, left: 0, bottom: 0 };
const XAXIS_STYLE_SM = { fontSize: '11px' };
const YAXIS_STYLE_XS = { fontSize: '10px' };
const YAXIS_DOMAIN_AUTO = ['auto', 'auto'];
const RSI_DOMAIN = [0, 100];
const RSI_TICKS = [30, 70];

const yTickINR = (v) => `₹${Math.round(v)}`;
const formatNumber = (v, name) => [typeof v === 'number' ? v.toFixed(2) : v, name];

const INDICATOR_LINES = [
  { key: 'sma20', name: 'SMA 20', stroke: '#FBBF24', width: 1.5, gate: 'SMA20' },
  { key: 'sma50', name: 'SMA 50', stroke: '#A855F7', width: 1.5, gate: 'SMA50' },
  { key: 'ema9', name: 'EMA 9', stroke: '#2563EB', width: 1.5, gate: 'EMA9' },
  { key: 'bollUpper', name: 'Boll Upper', stroke: 'rgba(148,163,184,0.5)', width: 1, gate: 'Bollinger' },
  { key: 'bollLower', name: 'Boll Lower', stroke: 'rgba(148,163,184,0.5)', width: 1, gate: 'Bollinger' },
];

export function PriceChart({ chartData, activeIndicators, pattern }) {
  return (
    <div className="bg-[#12141A] border border-white/10 p-4 mb-4" data-testid="price-chart">
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={chartData} margin={MAIN_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis dataKey="date" stroke="#64748B" style={XAXIS_STYLE_SM} interval={Math.floor(chartData.length / 10)} />
          <YAxis stroke="#64748B" style={XAXIS_STYLE_SM} domain={YAXIS_DOMAIN_AUTO} tickFormatter={yTickINR} />
          <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL} itemStyle={TOOLTIP_ITEM} formatter={formatNumber} />
          <Bar dataKey="range" fill="transparent" shape={<CandlestickBar />} />
          {INDICATOR_LINES.filter((l) => activeIndicators.includes(l.gate)).map((l) => (
            <Line key={l.key} type="monotone" dataKey={l.key} stroke={l.stroke} dot={false} strokeWidth={l.width} name={l.name} />
          ))}
          {pattern?.support && (
            <ReferenceLine y={pattern.support} stroke="#00FF66" strokeDasharray="3 3"
              label={{ value: `Support ₹${pattern.support}`, fill: '#00FF66', fontSize: 10 }} />
          )}
          {pattern?.resistance && (
            <ReferenceLine y={pattern.resistance} stroke="#FF3366" strokeDasharray="3 3"
              label={{ value: `Resistance ₹${pattern.resistance}`, fill: '#FF3366', fontSize: 10 }} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RSIMACDPanels({ chartData, chart }) {
  const latestRsi = chart.rsi[chart.rsi.length - 1]?.toFixed(2);
  const latestMacd = chart.macd.macd[chart.macd.macd.length - 1]?.toFixed(2);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div className="bg-[#12141A] border border-white/10 p-4" data-testid="rsi-chart">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">RSI (14)</div>
          <div className="font-mono text-sm text-white">{latestRsi}</div>
        </div>
        <ResponsiveContainer width="100%" height={120}>
          <ComposedChart data={chartData} margin={MINI_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="date" hide />
            <YAxis stroke="#64748B" style={YAXIS_STYLE_XS} domain={RSI_DOMAIN} ticks={RSI_TICKS} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL} itemStyle={TOOLTIP_ITEM} />
            <ReferenceLine y={70} stroke="#FF3366" strokeDasharray="3 3" />
            <ReferenceLine y={30} stroke="#00FF66" strokeDasharray="3 3" />
            <Line type="monotone" dataKey="rsi" stroke="#A855F7" dot={false} strokeWidth={2} name="RSI" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-[#12141A] border border-white/10 p-4" data-testid="macd-chart">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#94A3B8]">MACD (12,26,9)</div>
          <div className="font-mono text-sm text-white">{latestMacd}</div>
        </div>
        <ResponsiveContainer width="100%" height={120}>
          <ComposedChart data={chartData} margin={MINI_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
            <XAxis dataKey="date" hide />
            <YAxis stroke="#64748B" style={YAXIS_STYLE_XS} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL} itemStyle={TOOLTIP_ITEM} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
            <Bar dataKey="macdHist" name="Histogram">
              {chartData.map((entry) => (
                <Cell key={entry.date} fill={entry.macdHist >= 0 ? '#00FF66' : '#FF3366'} />
              ))}
            </Bar>
            <Line type="monotone" dataKey="macd" stroke="#2563EB" dot={false} strokeWidth={2} name="MACD" />
            <Line type="monotone" dataKey="macdSignal" stroke="#FBBF24" dot={false} strokeWidth={2} name="Signal" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
